import { Pressable } from "react-native";
import { View, Text } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@src/components/Icon";
import { sizes } from "@src/constants/sizes";
import { useLivestream } from "@src/features/cameras";
import { useStillWatching } from "@src/features/cameras/hooks/useStillWatching";
import { useRecordActivity } from "@visioni/a3s-activity/app";
import { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { track } from "@visioni/a3s-analytics";
import {
  cameraScope,
  LivestreamError,
  LivestreamStarted,
  LivestreamTimeout,
  LivestreamViewed,
} from "@src/features/cameras/analytics";
import { useTenantId } from "@src/features/tenants";
import { StillWatchingView } from "@src/features/cameras/components/StillWatchingView";
import { StreamTimeoutView } from "@src/features/cameras/components/StreamTimeoutView";
import { StreamErrorView } from "@src/features/cameras/components/StreamErrorView";

interface InlineLivestreamPlayerProps {
  siteId: string;
  cameraKey: string;
  onClose: () => void;
}

const STREAM_TIMEOUT_MS = 15_000;

const generateMp4HTML = (url: string) => `
  <!DOCTYPE html>
  <html>
  <head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      html, body { width: 100%; height: 100%; background: black; }
      video { width: 100%; height: 100%; object-fit: contain; }
    </style>
  </head>
  <body>
    <video autoplay controls muted playsinline>
      <source src="${url}" type="video/mp4">
    </video>
    <script>
      var reported = false;
      function reportError() {
        if (reported) return;
        reported = true;
        window.parent.postMessage({ type: 'livestream-error' }, '*');
      }
      document.querySelector('source').addEventListener('error', reportError);
      document.querySelector('video').addEventListener('error', reportError);
    </script>
  </body>
  </html>
`;

const generateMjpegHTML = (url: string) => `
  <!DOCTYPE html>
  <html>
  <head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      html, body { width: 100%; height: 100%; background: black; overflow: hidden; }
      img { width: 100%; height: 100%; object-fit: contain; }
    </style>
  </head>
  <body>
    <img src="${url}" alt="" />
    <script>
      document.querySelector('img').addEventListener('error', function() {
        window.parent.postMessage({ type: 'livestream-error' }, '*');
      });
    </script>
  </body>
  </html>
`;

const isMjpegFormat = (format?: string | null) => format === "mjpeg" || format === "http-mjpeg" || format === "mjpg";

export function InlineLivestreamPlayer({ siteId, cameraKey, onClose }: InlineLivestreamPlayerProps) {
  const { theme } = useUnistyles();
  const tenantId = useTenantId();
  const { camera, streamUrl, streamFormat, renewSession } = useLivestream({ siteId, cameraKey });

  const [isBuffering, setIsBuffering] = useState(true);
  const [streamTimedOut, setStreamTimedOut] = useState(false);
  const [streamError, setStreamError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const mountTimeRef = useRef(Date.now());
  const cameraDataReadyRef = useRef<number | null>(null);
  const streamUrlReadyRef = useRef<number | null>(null);
  const hasTrackedStart = useRef(false);
  const sessionGenRef = useRef(0);

  const prefersMjpeg = isMjpegFormat(streamFormat);

  const mediaViewPathParams = useMemo(
    () => ({
      siteId: SiteId.fromString(siteId),
      assetCameraKey: AssetCameraKey.fromString(cameraKey),
    }),
    [siteId, cameraKey],
  );
  const { mutate: recordActivity } = useRecordActivity(mediaViewPathParams);

  const { reset: resetStream, ...stillWatching } = useStillWatching({
    isActive: !isBuffering && !!streamUrl && !streamTimedOut && !streamError,
    siteId,
    cameraKey,
    onRefresh: () => {
      sessionGenRef.current++;
      setStreamTimedOut(false);
      setStreamError(false);
      setIsBuffering(true);
      hasTrackedStart.current = false;
      mountTimeRef.current = Date.now();
      cameraDataReadyRef.current = null;
      streamUrlReadyRef.current = null;
      void renewSession();
    },
    onClose,
  });

  const handleRetry = useCallback(() => {
    sessionGenRef.current++;
    setStreamTimedOut(false);
    setStreamError(false);
    setIsBuffering(true);
    hasTrackedStart.current = false;
    mountTimeRef.current = Date.now();
    cameraDataReadyRef.current = null;
    streamUrlReadyRef.current = null;
    resetStream();
    void renewSession();
  }, [renewSession, resetStream]);

  // Timing for `Livestream Started`, measured as in `LivestreamContent`. The camera time
  // is stamped once per attempt (a background refetch must not move it); retry and
  // refresh reset it to null.
  useEffect(() => {
    if (camera && cameraDataReadyRef.current === null) cameraDataReadyRef.current = Date.now();
  }, [camera]);

  useEffect(() => {
    if (streamUrl) streamUrlReadyRef.current = Date.now();
  }, [streamUrl]);

  // Reset buffering state when stream URL changes
  useEffect(() => {
    if (streamUrl) {
      setIsBuffering(true);
      setStreamTimedOut(false);
      setStreamError(false);
      resetStream();
    }
  }, [streamUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stream connection timeout — if buffering persists for 15s after streamUrl is set
  useEffect(() => {
    if (!streamUrl || !isBuffering || streamTimedOut) return;

    const timer = setTimeout(() => {
      setStreamTimedOut(true);
      track(LivestreamTimeout, {
        ...cameraScope(tenantId, siteId, cameraKey),
        duration_ms: Date.now() - mountTimeRef.current,
      });
    }, STREAM_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [streamUrl, isBuffering, streamTimedOut, cameraKey, tenantId, siteId]);

  // Listen for stream errors from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "livestream-error") {
        setStreamError(true);
        setIsBuffering(false);
        resetStream();
        track(LivestreamError, {
          ...cameraScope(tenantId, siteId, cameraKey),
          error_type: "playback_failed",
          duration_ms: Date.now() - mountTimeRef.current,
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [cameraKey, tenantId, siteId, resetStream]);

  // Track stream started when buffering completes
  useEffect(() => {
    if (!isBuffering && streamUrl && !hasTrackedStart.current) {
      hasTrackedStart.current = true;
      const now = Date.now();
      const cameraDataReadyAt = cameraDataReadyRef.current;
      const streamUrlReadyAt = streamUrlReadyRef.current;
      track(LivestreamStarted, {
        ...cameraScope(tenantId, siteId, cameraKey),
        duration_ms: now - mountTimeRef.current,
        camera_data_ready_ms: cameraDataReadyAt === null ? null : cameraDataReadyAt - mountTimeRef.current,
        stream_url_ready_ms: streamUrlReadyAt === null ? null : streamUrlReadyAt - mountTimeRef.current,
        stream_connect_ms: streamUrlReadyAt === null ? null : now - streamUrlReadyAt,
      });
      recordActivity(
        { kind: "livestream_viewed" },
        {
          onError: (error) => {
            console.warn("Failed to record livestream view activity:", error);
          },
        },
      );
    }
  }, [isBuffering, streamUrl, cameraKey, tenantId, siteId, recordActivity]);

  // Track livestream viewed on mount
  useEffect(() => {
    track(LivestreamViewed, cameraScope(tenantId, siteId, cameraKey));
  }, [cameraKey, tenantId, siteId]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const handleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (container.requestFullscreen) {
      void container.requestFullscreen();
    }
  }, []);

  const handleExitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    }
  }, []);

  if (streamTimedOut) {
    return (
      <View
        width="100%"
        height={sizes.webSnapshotHeight}
        borderRadius={theme.spacing.md}
        bg="black"
        style={{ overflow: "hidden", position: "relative" }}
      >
        <StreamTimeoutView onRetry={handleRetry} />
      </View>
    );
  }

  if (streamError) {
    return (
      <View
        width="100%"
        height={sizes.webSnapshotHeight}
        borderRadius={theme.spacing.md}
        bg="black"
        style={{ overflow: "hidden", position: "relative" }}
      >
        <StreamErrorView onRetry={handleRetry} />
      </View>
    );
  }

  return (
    <View
      ref={containerRef as any}
      width="100%"
      height={isFullscreen ? "100%" : sizes.webSnapshotHeight}
      borderRadius={isFullscreen ? 0 : theme.spacing.md}
      style={{ overflow: "hidden", position: "relative", backgroundColor: "#000" }}
    >
      {streamUrl ?
        <iframe
          key={sessionGenRef.current}
          ref={iframeRef as any}
          srcDoc={prefersMjpeg ? generateMjpegHTML(streamUrl) : generateMp4HTML(streamUrl)}
          style={{ width: "100%", height: "100%", border: "none" }}
          allow="autoplay; fullscreen"
          onLoad={() => setIsBuffering(false)}
        />
      : <View flex={1} alignItems="center" justifyContent="center">
          <Text textStyle="sm/medium" color="white">
            Loading stream...
          </Text>
        </View>
      }

      {stillWatching.showPrompt && (
        <StillWatchingView
          autoStopSeconds={stillWatching.autoStopSeconds}
          onContinue={stillWatching.handleContinueWatching}
          onStop={stillWatching.handleStopWatching}
        />
      )}

      {/* Controls overlay */}
      <View
        style={{
          position: "absolute",
          top: theme.spacing.xl,
          right: theme.spacing.xl,
          flexDirection: "row",
          gap: theme.spacing.md,
          zIndex: 10,
        }}
      >
        {isFullscreen ?
          <Pressable
            onPress={handleExitFullscreen}
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              backgroundColor: theme.colors.mediaOverlay.backgroundMedium,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name="fullscreenExit" size={18} color={theme.colors.white.default} />
          </Pressable>
        : <Pressable
            onPress={handleFullscreen}
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              backgroundColor: theme.colors.mediaOverlay.backgroundMedium,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name="fullscreen" size={18} color={theme.colors.white.default} />
          </Pressable>
        }
        <Pressable
          onPress={onClose}
          style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            backgroundColor: theme.colors.mediaOverlay.backgroundMedium,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name="close" size={28} color={theme.colors.white.default} />
        </Pressable>
      </View>
    </View>
  );
}
